import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { ToasterService } from '@root/packages/albert/src/components/toaster/toaster.service';
import {
  DatadogContext,
  DatadogService,
  sentrySetUser,
  ThamService,
  UnleashContext,
  UnleashService,
} from '@thiememeulenhoff/ng-sdk';
import {
  AuthService,
  PlatformService,
  UserService,
  WebappService,
} from '@thiememeulenhoff/ng-security';
import { translate } from '@thiememeulenhoff/translations';
import { UserInterface } from '@thiememeulenhoff/types';
import { filter, map, switchMap, tap } from 'rxjs';
import { RunModeParam, RunModes } from '../_utils/run-mode';
import { ThasService } from './thas.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  readonly #userService = inject(UserService);

  readonly #authService = inject(AuthService);

  readonly #webappService = inject(WebappService);

  readonly #activatedRoute = inject(ActivatedRoute);

  readonly #unleashService = inject(UnleashService);

  readonly #datadogService = inject(DatadogService);

  readonly #thasService = inject(ThasService);

  readonly #platformService = inject(PlatformService);

  readonly #thamService = inject(ThamService);

  readonly user$ = this.#userService.user$;

  readonly hasAdminLicense$ = this.user$.pipe(
    filter((activeUser) => !!activeUser),
    map((activeUser) => {
      return !!activeUser?.licenses.find((license) => {
        return license.cpi === 'edition_admin' && license.state === 'ACTIVE';
      });
    }),
  );

  readonly isTeacher$ = this.user$.pipe(
    filter((activeUser) => !!activeUser),
    map((activeUser) => {
      return !!activeUser?.roles?.find(
        (role) => role === 'thiememeulenhoff/license/teacher',
      );
    }),
  );

  readonly isStudent$ = this.user$.pipe(
    filter((activeUser) => !!activeUser),
    map((activeUser) => {
      return !!activeUser?.roles?.find(
        (role) => role === 'thiememeulenhoff/license/student',
      );
    }),
  );

  constructor() {
    this.#platformService.accessToken$.subscribe((accessToken) => {
      this.#thamService.accessToken = accessToken;
    });
  }

  isTeacher() {
    return Boolean(
      this.#userService.user?.roles?.find(
        (role) => role === 'thiememeulenhoff/license/teacher',
      ),
    );
  }

  login() {
    if (this.#getRunMode() !== RunModes.standalone) {
      return this.#loadUser();
    }

    return this.#authService.login().pipe(switchMap(() => this.#loadUser()));
  }

  refresh(showToasts = false) {
    const currentUser = this.#userService.user;

    // Always load the user first
    return this.#loadUser(true).pipe(
      switchMap((newUser) => {
        if (this.#getRunMode() !== RunModes.standalone) {
          return this.#webappService.login(true).pipe(map(() => newUser));
        }
        return this.#authService.refresh().pipe(map(() => newUser));
      }),
      tap((newUser) => {
        if (!showToasts || !currentUser) {
          return;
        }
        const changed =
          JSON.stringify(newUser.licenses) !==
          JSON.stringify(currentUser.licenses);

        ToasterService.Toast({
          title: translate(
            changed
              ? 'layout.header.user.renewed_licenses'
              : 'layout.header.user.renewed_licenses_no_new_licenses',
          ),
          type: changed ? 'success' : 'info',
          icon: changed ? 'circle-check-solid' : 'circle-info-solid',
        });
      }),
    );
  }

  logout() {
    this.#authService.logout();
    this.#userService.setUser(undefined);
  }

  get user() {
    return this.#userService.user;
  }

  get userIdentifier() {
    if (!this.user) {
      throw new Error('User is not authenticated');
    }

    return this.user.identifier;
  }

  #getRunMode() {
    return (
      this.#getRunModeForSnapshot(this.#activatedRoute.snapshot) ??
      RunModes.standalone
    );
  }

  // eslint-disable-next-line class-methods-use-this
  #getRunModeForSnapshot(snapshot: ActivatedRouteSnapshot) {
    return (
      snapshot.data[RunModeParam] ?? snapshot.firstChild?.data[RunModeParam]
    );
  }

  #loadUser(renew = false) {
    return this.#userService.fetchUser(renew).pipe(
      tap((user) => this.#thasService.setActor(user)),
      tap(async (user) => this.#setupUnleashContext(user)),
      tap(async (user) => this.#setupDatadogContext(user)),
      tap((user) => sentrySetUser(user?.identifier)),
    );
  }

  #previousContextString = '';

  async #setupUnleashContext(user: UserInterface) {
    const nextContext = {
      [UnleashContext.UserReference]: user.identifier,
      [UnleashContext.Cpis]: user.licenses
        .map((license) => license.cpi)
        .join(','),
      ...(user.brin ? { [UnleashContext.BrinCode]: user.brin } : {}),
    };

    const nextContextString = JSON.stringify(nextContext);

    if (nextContextString !== this.#previousContextString) {
      await this.#unleashService.addContext(nextContext);
    }

    await this.#unleashService.start();

    this.#previousContextString = JSON.stringify(nextContext);
  }

  async #setupDatadogContext(user: UserInterface) {
    this.#datadogService.setUser({
      [DatadogContext.UserReference]: user.identifier,
      [DatadogContext.Cpis]: user.licenses.map((license) => license.cpi),
      ...(user.brin ? { [DatadogContext.BrinCode]: user.brin } : {}),
      ...(user.roles
        ? {
            [DatadogContext.Role]: user.roles.includes(
              'thiememeulenhoff/license/teacher',
            )
              ? 'teacher'
              : 'student',
          }
        : {}),
    });
  }
}
